<template>
  <v-dialog v-model="dialog" max-width="500px">
    <template v-slot:activator="{ props }">
      <v-btn color="primary" variant="outlined" v-bind="props" class="filter-btn mt-4" style="background-color: #ffffff;">
        <v-icon>mdi-filter</v-icon>
        <v-chip v-if="selectedClassIds.length > 0" class="ml-2" size="x-small" color="primary">
          {{ selectedClassIds.length }}
        </v-chip>
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h6">{{ $t('filter.classFilter') }}</span>
      </v-card-title>

      <v-card-text>
        <v-select
          v-model="selectedYear"
          :items="yearOptions"
          :label="$t('filter.fiscalYear')"
          variant="outlined"
          density="compact"
          class="mb-2"
          item-title="text"
          item-value="value"
          color="primary"
          hide-details="auto"
        ></v-select>

        <div style="max-height: 300px; overflow-y: auto;">
          <v-list density="compact">
            <v-list-item
              v-for="nurseryClass in filteredClasses"
              :key="nurseryClass.classId"
              :value="nurseryClass.classId"
              color="primary"
              @click="toggleSelection(nurseryClass.classId)"
              :active="selectedIndexes.includes(nurseryClass.classId)"
            >
              <template v-slot:prepend>
                <v-list-item-action start>
                  <v-checkbox-btn 
                    :model-value="selectedIndexes.includes(nurseryClass.classId)"
                    color="primary"
                  ></v-checkbox-btn>
                </v-list-item-action>
              </template>

              <v-list-item-title>
                {{ nurseryClass.className }}
              </v-list-item-title>
            </v-list-item>
          </v-list>

          <div v-if="filteredClasses.length === 0" class="text-center text-grey pa-4">
            {{ $t('filter.noClassesInYear') }}
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn variant="text" color="grey" @click="clearFilter">{{ $t('filter.clear') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn variant="text" color="grey" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
        <v-btn variant="text" color="primary" @click="applyFilter">{{ $t('filter.apply') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { NurseryClassResponse } from 'chaild-api/lib';
import { getFiscalYear } from '@/service/CommonUtils';
import { i18n } from '@/plugins/i18n';

export default defineComponent({
  name: 'ClassFilterDialog',
  emits: ['update:modelValue', 'apply'],
  props: {
    classes: {
      type: Array as () => NurseryClassResponse[],
      required: true,
    },
    modelValue: {
      type: Array as () => number[],
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      selectedIndexes: [] as number[],
      selectedYear: Number(getFiscalYear()) as number,
    };
  },
  computed: {
    selectedClassIds(): number[] {
      return this.modelValue;
    },
    yearOptions(): { text: string; value: number }[] {
      if (!this.classes || this.classes.length === 0) {
        return [];
      }
      const years = this.classes
        .map(c => c.year)
        .filter((y): y is number => typeof y === 'number' && !isNaN(y) && y > 0);
      const uniqueYears = [...new Set(years)];
      uniqueYears.sort((a, b) => b - a);
      return uniqueYears.map(year => ({
        text: i18n.global.t('filter.yearFormat', { year }),
        value: year,
      }));
    },
    filteredClasses(): NurseryClassResponse[] {
      if (!this.classes || this.classes.length === 0) {
        return [];
      }
      return this.classes.filter(c => c.year === this.selectedYear);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal: number[]) {
        this.selectedIndexes = [...newVal];
      },
    },
    dialog(newVal: boolean) {
      if (newVal) {
        this.initializeSelectedYear();
      }
    },
    classes: {
      immediate: true,
      handler() {
        if (this.yearOptions.length > 0 && !this.yearOptions.some(y => y.value === this.selectedYear)) {
          this.selectedYear = this.yearOptions[0]?.value ?? Number(getFiscalYear());
        }
      },
    },
  },
  methods: {
    toggleSelection(id: number) {
      if (this.selectedIndexes.includes(id)) {
        this.selectedIndexes = this.selectedIndexes.filter(i => i !== id);
      } else {
        this.selectedIndexes.push(id);
      }
    },

    initializeSelectedYear() {
      if (this.selectedClassIds.length > 0) {
        const selectedClass = this.classes.find(c => this.selectedClassIds.includes(c.classId));
        if (selectedClass?.year) {
          this.selectedYear = selectedClass.year;
        }
      } else if (this.yearOptions.length > 0) {
        const currentYear = Number(getFiscalYear());
        if (this.yearOptions.some(y => y.value === currentYear)) {
          this.selectedYear = currentYear;
        } else {
          this.selectedYear = this.yearOptions[0].value;
        }
      }
    },
    applyFilter() {
      this.$emit('update:modelValue', this.selectedIndexes);
      this.$emit('apply');
      this.dialog = false;
    },
    clearFilter() {
      this.selectedIndexes = [];
    },
    closeDialog() {
      this.selectedIndexes = [...this.selectedClassIds];
      this.dialog = false;
    },
  },
});
</script>

<style scoped>
.filter-btn {
  margin-bottom: 16px;
}
</style>